import { useCallback, useState } from 'react';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { EVENT_PAGE, SUGGESTED_AUDIENCES } from 'src/lib/events';
import { useErrorHandler } from 'src/services';
import { useBrandVoicesActions, useCustomerPersonaActions } from 'src/store';
import { useBrandVoicePopup } from 'src/views/BrandVoicePopup';

import { useTrackingEvent } from './useTrackingEvent';

export const useAddCustomerPersonaToMyAudiences = () => {
  const customerPersonaStoreActions = useCustomerPersonaActions();
  const handleError = useErrorHandler();
  const trackEvent = useTrackingEvent();
  const openConfirmPopup = useConfirmPopup();
  const openBrandVoicePopup = useBrandVoicePopup();
  const brandVoicesActions = useBrandVoicesActions();

  const [brandVoiceToUse, setBrandVoiceToUse] = useState(null);

  return useCallback(
    async (brandVoice, customerPersona, onSave, language) => {
      if (brandVoice) {
        setBrandVoiceToUse(brandVoice);
      }
      if (customerPersona?.isSuggested) {
        trackEvent(SUGGESTED_AUDIENCES.saved, {
          page: EVENT_PAGE.editor,
        });
      }

      try {
        let newBrandVoice = null;
        if (!brandVoice) {
          const confirmed = await openConfirmPopup({
            mode: CONFIRM_POPUP_MODE.confirmation,
            title: "Target audience language doesn't match brand voice",
            content: 'To save this target audience, create a brand voice in the same language.',
            buttons: {
              confirm: 'Create brand voice',
              cancel: 'Close',
            },
          });
          if (!confirmed) {
            return;
          }
          newBrandVoice = await openBrandVoicePopup({
            noRedirect: true,
            language,
            lockLanguage: true,
          });
          setBrandVoiceToUse(newBrandVoice);
          if (!newBrandVoice) {
            return;
          }

          await brandVoicesActions.queryBrandVoicesAssets(newBrandVoice.id);
        }

        const updatedPersona = await customerPersonaStoreActions.saveSuggestedPersona(
          newBrandVoice || brandVoiceToUse || brandVoice,
          customerPersona
        );
        onSave(updatedPersona);
        return updatedPersona;
      } catch (error) {
        handleError(error);
      }
    },
    [
      trackEvent,
      customerPersonaStoreActions,
      handleError,
      openBrandVoicePopup,
      openConfirmPopup,
      brandVoiceToUse,
      brandVoicesActions,
    ]
  );
};
